import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface LoadingSkeletonProps {
  className?: string;
}

// Generic card skeleton
export const CardSkeleton: React.FC<LoadingSkeletonProps> = ({ className = "" }) => (
  <Card className={`${className}`}>
    <CardHeader>
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-3 w-1/2" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-3 w-full mb-2" />
      <Skeleton className="h-3 w-4/5" />
    </CardContent>
  </Card>
);

// Workflow card skeleton
export const WorkflowCardSkeleton: React.FC<LoadingSkeletonProps> = ({ className = "" }) => (
  <Card className={`${className}`}>
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Flow card skeleton
export const FlowCardSkeleton: React.FC<LoadingSkeletonProps> = ({ className = "" }) => (
  <Card className={`${className}`}>
    <CardContent className="p-6">
      <div className="flex items-start gap-4">
        <Skeleton className="w-12 h-12 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-3">
          <div className="space-y-2">
            <Skeleton className="h-5 w-2/3" />
            <Skeleton className="h-4 w-full" />
          </div>
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Integration node card skeleton
export const NodeCardSkeleton: React.FC<LoadingSkeletonProps> = ({ className = "" }) => (
  <div className={`bg-white p-4 rounded-xl border border-slate-200 shadow-sm ${className}`}>
    <div className="flex items-start gap-4">
      <Skeleton className="w-8 h-8 rounded flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    </div>
  </div>
);

// Gallery grid skeleton
export const GalleryGridSkeleton: React.FC<{ 
  count?: number;
  type?: 'workflow' | 'flow' | 'node';
  className?: string;
}> = ({ count = 6, type = 'workflow', className = "" }) => {
  const SkeletonComponent = {
    workflow: WorkflowCardSkeleton,
    flow: FlowCardSkeleton,
    node: NodeCardSkeleton
  }[type];

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6 ${className}`}>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonComponent key={index} />
      ))}
    </div>
  );
};

// Detail page hero skeleton
export const DetailHeroSkeleton: React.FC<LoadingSkeletonProps> = ({ className = "" }) => (
  <div className={`bg-white dark:bg-slate-800 border-b ${className}`}>
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <Skeleton className="h-10 w-32 mb-6" />
        <div className="flex flex-col md:flex-row items-start md:justify-between mb-8">
          <div className="flex items-start gap-6 flex-1">
            <Skeleton className="w-16 h-16 rounded-xl flex-shrink-0" />
            <div className="space-y-4 flex-1">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-2/3" />
              <div className="flex gap-3">
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-6 w-24 rounded-full" />
              </div>
            </div>
          </div>
          <div className="flex gap-3 mt-4 md:mt-0">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-28" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// List item skeleton
export const ListItemSkeleton: React.FC<LoadingSkeletonProps> = ({ className = "" }) => (
  <div className={`flex items-center gap-4 p-4 border-b last:border-b-0 ${className}`}>
    <Skeleton className="w-12 h-12 rounded-lg flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
    <Skeleton className="h-8 w-20" />
  </div>
);

// Search results skeleton
export const SearchResultsSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, index) => (
      <ListItemSkeleton key={index} />
    ))}
  </div>
);

// Page skeleton with header and content
export const PageSkeleton: React.FC<{
  showHero?: boolean;
  showSidebar?: boolean;
  contentType?: 'grid' | 'list' | 'detail';
}> = ({ showHero = false, showSidebar = false, contentType = 'grid' }) => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 to-violet-50 dark:from-slate-900 dark:to-violet-900">
    {showHero && <DetailHeroSkeleton />}
    
    <div className="container mx-auto px-4 py-12">
      <div className={`flex ${showSidebar ? 'flex-col lg:flex-row gap-8' : ''}`}>
        {showSidebar && (
          <aside className="w-full lg:w-80">
            <Card className="p-6">
              <Skeleton className="h-6 w-24 mb-4" />
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            </Card>
          </aside>
        )}
        
        <main className="flex-1">
          {contentType === 'grid' && <GalleryGridSkeleton />}
          {contentType === 'list' && <SearchResultsSkeleton />}
          {contentType === 'detail' && (
            <div className="space-y-8">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          )}
        </main>
      </div>
    </div>
  </div>
);